import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const EMPTY_STATE: DesktopUpdaterState = {
  supported: false,
  enabled: false,
  status: 'idle',
  currentVersion: '',
  latestVersion: '',
  progressPercent: 0,
  transferredBytes: 0,
  totalBytes: 0,
  message: '',
};

export function useAppUpdate() {
  const state = ref<DesktopUpdaterState>({ ...EMPTY_STATE });
  const ready = ref(false);
  let unsubscribe = () => {};

  const bannerVisible = computed(() => {
    if (!ready.value || !state.value.supported) {
      return false;
    }

    return ['checking', 'downloading', 'downloaded', 'error'].includes(state.value.status);
  });

  const bannerText = computed(() => {
    if (state.value.status === 'downloading') {
      const versionLabel = state.value.latestVersion ? ` ${state.value.latestVersion}` : '';
      const progressLabel = state.value.progressPercent > 0 ? ` (${state.value.progressPercent}%)` : '';
      return `Загружаем обновление${versionLabel}${progressLabel}`;
    }

    if (state.value.status === 'downloaded') {
      const versionLabel = state.value.latestVersion ? ` ${state.value.latestVersion}` : '';
      return `Обновление${versionLabel} готово к установке`;
    }

    if (state.value.status === 'checking') {
      return 'Проверяем обновления приложения';
    }

    if (state.value.status === 'error') {
      return state.value.message || 'Не удалось обновить приложение';
    }

    return state.value.message || '';
  });

  const actionLabel = computed(() => {
    if (state.value.status === 'downloaded') {
      return 'Перезапустить и обновить';
    }

    if (state.value.status === 'error') {
      return 'Повторить';
    }

    return '';
  });

  const actionVisible = computed(() => ['downloaded', 'error'].includes(state.value.status));

  async function action() {
    if (!window.__CAFE_DESKTOP_UPDATER__) {
      return;
    }

    if (state.value.status === 'downloaded') {
      await window.__CAFE_DESKTOP_UPDATER__.quitAndInstall();
      return;
    }

    if (state.value.status === 'error') {
      state.value = await window.__CAFE_DESKTOP_UPDATER__.checkForUpdates();
    }
  }

  onMounted(async () => {
    if (!window.__CAFE_DESKTOP_RUNTIME__ || !window.__CAFE_DESKTOP_UPDATER__) {
      return;
    }

    ready.value = true;
    state.value = await window.__CAFE_DESKTOP_UPDATER__.getState();
    unsubscribe = window.__CAFE_DESKTOP_UPDATER__.subscribe((nextState) => {
      state.value = nextState;
    });
  });

  onBeforeUnmount(() => {
    unsubscribe();
  });

  return {
    bannerVisible,
    bannerText,
    actionLabel,
    actionVisible,
    action,
    updateState: state,
  };
}
